import { doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getFirestoreDb } from '@/lib/firebase';
import { useAsyncSave } from '@/components/settings/useAsyncSave';
import { useOrgSettingsDoc } from '@/components/settings/useOrgSettingsDoc';
import { useSettingsAccess } from '@/components/settings/useSettingsAccess';
import { SettingsFirestoreError } from '@/components/settings/SettingsFirestoreError';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { SettingsSection } from '@/components/settings/SettingsSection';

export function TaxGlSection() {
  const { t } = useTranslation();
  const { orgId, online, canEdit } = useSettingsAccess();
  const { raw, loading, error } = useOrgSettingsDoc(orgId, 'tax');
  const { saving, run } = useAsyncSave('settings-tax-gl');
  const [outputCode, setOutputCode] = useState('2200');
  const [inputCode, setInputCode] = useState('2201');

  useEffect(() => {
    if (!raw) return;
    setOutputCode(String(raw.vatOutputAccountCode ?? '2200'));
    setInputCode(String(raw.vatInputAccountCode ?? '2201'));
  }, [raw]);

  async function save() {
    if (!orgId || !canEdit || !online) return;
    await run(async () => {
      await setDoc(
        doc(getFirestoreDb(), 'organizations', orgId, 'settings', 'tax'),
        {
          vatOutputAccountCode: outputCode.trim(),
          vatInputAccountCode: inputCode.trim(),
          updatedAt: serverTimestamp()
        },
        { merge: true }
      );
    }, t('settings.taxGlSaved'));
  }

  return (
    <SettingsSection
      id="settings-tax-gl"
      lazy
      title={t('settings.taxGl')}
      description={t('settings.taxGlHint')}
      loading={loading}
    >
      {(visible) =>
        visible ? (
          <>
            <SettingsFirestoreError message={error ?? undefined} />
            <div>
              <Label htmlFor="vat-output">{t('settings.vatOutputAccount')}</Label>
              <Input
                id="vat-output"
                className="mt-1 max-w-xs"
                value={outputCode}
                disabled={!canEdit || !online}
                onChange={(e) => setOutputCode(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="vat-input">{t('settings.vatInputAccount')}</Label>
              <Input
                id="vat-input"
                className="mt-1 max-w-xs"
                value={inputCode}
                disabled={!canEdit || !online}
                onChange={(e) => setInputCode(e.target.value)}
              />
            </div>
            {canEdit && (
              <Button variant="secondary" disabled={!online || saving} onClick={() => void save()}>
                {t('common.save')}
              </Button>
            )}
          </>
        ) : null
      }
    </SettingsSection>
  );
}
